import { Injectable } from '@angular/core';
import { SessionService } from '../session/session.service';
import { ContainerService } from '../container/container.service';
import { Container, ReportDetailsByJurisdiction } from '../../models';
import { Subject } from 'rxjs';
import { Router, NavigationEnd } from '@angular/router';
import {
  ComplianceCentralService as ComplianceCentralServiceV1,
  PlatformService as PlatformServiceV1,
} from '../../../shared/services'; // TODO: Fix / bring this in locally.
import { CountriesWithUniqueMasterFileTemplates } from '@xbs/xbs-enums';
import { REPORT_TYPES } from '../../constants';

@Injectable({
  providedIn: 'root'
})
export class AppService {
  containerObservee;
  currentUrl: any;
  navigationHistory: string[] = [];
  onContainerChanged: Subject<Container>;
  onRouteChanged: Subject<string>;
  reports = {};
  routeChangedObservee;
  previousUrl: any;
  ContainersModule: any;

  public get containers(): Container[] {
    return this.getContainers();
  }

  public get currentContainer(): Container {
    return this.getCurrentContainer();
  }

  constructor(private _sessionService: SessionService,
    private _containersService: ContainerService,
    private _complianceCentralService: ComplianceCentralServiceV1,
    private _router: Router,
    private _platformService: PlatformServiceV1,
  ) {
    this.onRouteChanged = new Subject();
    this.onContainerChanged = new Subject();
    this.addRouteChangeListener();
    this.ContainersModule = this._platformService.getPlatformModule().Containers;
  }

  /**
   * Adds a listener that creates a stack of visited URL's.  This is handy when
   * a user wants to go back or forward in navigation history.
   * @todo Implement this on the entity detail page more properly.
   * @todo There should be a limit to number of saved sates (e.g. 80).
   */
  addRouteChangeListener() {
    this.currentUrl = this._router.url;
    this._router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.previousUrl = this.currentUrl;
        this.currentUrl = event.url;
        if (this.navigationHistory.length > 0) {
          const lastState = this.navigationHistory[this
            .navigationHistory.length - 1];
          if (this.previousUrl && this.previousUrl !== lastState) {
            this.navigationHistory.push(this.previousUrl);
          }
        }
        this.navigationHistory.push(this.currentUrl);
        if (this.onRouteChanged) {
          this.onRouteChanged.next(this.currentUrl);
        }
      };
    });
  }

  /**
   * Changes the current container to some new container.  When the container
   * is changed, the associated `onContainerChanged` subject is provided a new
   * message with the newly assigned `currentContainer`.
   */
  async changeContainer(container) {
    const currentContainer = this.containers.find((cont) => cont.containerId === container.value);
    await this.ContainersModule.switchContainers(currentContainer);
    this._containersService.getWorkingContainerEntity()
      .subscribe(({ data: upe }) => {
        this._sessionService.setWorkingContainer(currentContainer);
        const upeId = upe.entityId || 0;
        const upeCurrency = upe.upeCurrency || null;
        const currencyScale = upe.upeCurrencyScale || 0;
        this._sessionService.setContainerUpeId(upeId);
        this._sessionService.setContainerUpeCurrency(upeCurrency);
        this._sessionService.setContainerUpeCurrencyScale(currencyScale);
        this.onContainerChanged.next(currentContainer);
      });
  }

  /**
   * Gets the list of containers.
   */
  getContainers() {
    return this._sessionService.getContainersList();
  }

  /**
   * Gets the currently selected container.
   */
  getCurrentContainer() {
    return this._sessionService.getWorkingContainer();
  }

  /**
   * For the current container, get all of the reports for a provided country
   * ID.  The reason this exists on AppService is because it is used in both
   * the Entity Detail View AND the Compliance Central Quick View.
   */
  getReports(countryId: number, reportType: string, countryIso?: string): Promise<ReportDetailsByJurisdiction[]> {
    const containerId = this.getCurrentContainer().containerId;

    if (reportType === REPORT_TYPES.masterFile && !CountriesWithUniqueMasterFileTemplates.ByIso[countryIso]) {
      return new Promise((resolve) => {
        this._complianceCentralService.getOecdReportDetails(containerId).subscribe(({ data }) => {
          if (!this.reports[containerId]) {
            this.reports[containerId] = {};
          }
          this.reports[containerId][countryId] = data;
          resolve(data);
        });
      });
    } else {
      return new Promise((resolve) => {
        this._complianceCentralService.getReportDetailsByJurisdiction(containerId, countryId).subscribe(({ data }) => {
          if (!this.reports[containerId]) {
            this.reports[containerId] = {};
          }
          this.reports[containerId][countryId] = data;
          resolve(data);
        });
      });

    }
  }
}
